import { useCallback, useMemo } from 'react';
import { UTCDate } from '@date-fns/utc';
import { BarChart, type BarDatumRecord } from '@/shared/components/charts/BarChart';
import { ExportCsvButton } from '@/shared/components/ExportCSVButton';
import { ChartLegend } from '@/shared/components/molecules/ChartLegend';
import { type ChartAggregationIntervalOption } from '@/shared/hooks/useChartAggregation';
import { getTooltipDateLabel } from '@/shared/utils/date';

type ChartDatum = {
  date: string;
  rewards: string;
};

const color = '#FF33AF';

const BarTooltip = ({
  data,
  aggregationInterval,
}: {
  data: BarDatumRecord;
  aggregationInterval: ChartAggregationIntervalOption['value'];
}) => (
  <div className="flex w-[260px] flex-col font-aeonikMedium text-12 text-cf-light-2">
    <div className="font-aeonikMedium text-12">
      {getTooltipDateLabel({
        date: new UTCDate(data.date),
        aggregationInterval,
      })}
    </div>
    <span className="text-20 text-white">{data.rewards as string}</span>
  </div>
);

export const RewardsChart = ({
  data: chartData,
  isLoading,
  aggregationInterval,
  exportCsvData,
}: {
  data: ChartDatum[];
  aggregationInterval: ChartAggregationIntervalOption['value'];
  exportCsvData?: () => Promise<void>;
  isLoading?: boolean;
}) => {
  const chartDates = useMemo(
    () => [...new Set(chartData.map((item) => new Date(item.date).toISOString().split('T')[0]))],
    [chartData],
  );

  const renderTooltip = useCallback(
    (datum: BarDatumRecord) => (
      <BarTooltip data={datum} aggregationInterval={aggregationInterval} />
    ),
    [aggregationInterval],
  );

  const loading = Boolean(isLoading || !chartData);

  return (
    <div className="flex-grow [&_rect]:transition [&_rect]:duration-150 [&_rect]:ease-[cubic-bezier(0.550,0.055,0.675,0.19)]">
      <BarChart
        chartId="stake-rewards"
        className="-mx-4 h-[300px]"
        data={chartData}
        keys={['rewards']}
        barColors={[color]}
        chartDates={chartDates}
        isLoading={loading}
        margin={{ top: 20, right: 20, bottom: 50, left: 60 }}
        padding={0.1}
        renderTooltip={renderTooltip}
      />
      <div className="flex justify-between">
        <ChartLegend
          items={[
            {
              label: <div className="flex items-center gap-x-1">Rewards</div>,
              color,
            },
          ]}
        />
        <ExportCsvButton
          label="Export CSV"
          canExport={Boolean(exportCsvData)}
          onExport={() => exportCsvData?.()}
        />
      </div>
    </div>
  );
};
